<template>
	<v-row>
		<v-col>
			<v-form
					v-model="formIsValid.dates"
					ref="datesForm"
					class="mb-8"
			>
				<ui-box
						:eager="!!localPass?.template_id"
						:form="$refs.datesForm"
						new-forms
				>
					<form-title title="Dates Someone Can Sign Up for the Pass">
						If you need to limit when this pass is available for sign up/purchase, add date ranges here.
						<template #button>
							<v-btn
								class="mt-1 rounded-lg"
								color="secondary"
								depressed
								outlined
								small
								@click="addRange"
							>
								<v-icon>
									mdi-plus
								</v-icon>
								Add Range
							</v-btn>
						</template>
					</form-title>

					<form-divider v-if="localPass.active_dates && localPass.active_dates.length" inner/>

					<template v-for="(activeDates, idx) in localPass.active_dates">
						<v-row :key="idx">
							<v-col>
								<bw-date-time-picker
										v-model="activeDates.start_date"
										class="rounded-lg"
										clearable
										label="Start Date & Time"
										:max="activeDates.end_date"
										:tz="partner.timezone"
										withTime
										@cleared="activeDates.start_date = ''"
								></bw-date-time-picker>
							</v-col>
							<v-col>
								<bw-date-time-picker
										class="rounded-lg"
										clearable
										v-model="activeDates.end_date"
										label="End Date & Time"
										:min="activeDates.start_date"
										:tz="partner.timezone"
										withTime
										@input="(date) => onEndDateInput(date, activeDates)"
										@cleared="activeDates.end_date = ''"
								></bw-date-time-picker>
							</v-col>
							<v-col cols="1" class="d-flex align-center">
								<v-btn
										color="error"
										depressed
										fab
										outlined
										small
										@click="removeRange(idx)"
								>
									<v-icon>
										mdi-trash-can-outline
									</v-icon>
								</v-btn>
							</v-col>
						</v-row>
					</template>
				</ui-box>
			</v-form>

			<form-divider/>

			<v-form
					v-model="formIsValid.redemptions"
					ref="redemptionsForm"
					class="mb-8"
			>
				<!-- Advanced Pass Configuration -->
				<v-row>
					<v-col>
						<ui-box :form="$refs.redemptionsForm" :eager="!!localPass.id" new-forms>

							<form-title title="When Someone Can Use the Pass">
								These are the dates that a pass holder will be able to use their pass.
							</form-title>

							<form-divider inner/>

							<v-row>
								<v-col cols="12" sm="4">
									<v-select
											v-model="localPass.redemption_policy.activation_type"
											class="rounded-lg required"
											hide-details="auto"
											:items="activationTypes"
											item-text="name"
											label="Start Date"
											outlined
											:rules="rules.redemption_start"
									></v-select>
								</v-col>

								<!-- first use -->
								<v-col
										v-if="localPass.redemption_policy.activation_type === 'first_use'"
										sm="4"
								>
									<v-select
											v-model="localPass.redemption_policy.expires.type"
											class="rounded-lg required"
											hide-details="auto"
											:items="[
											{name: 'Days After Purchase', value: 'days_after_purchase'}, 
											{name: 'Date', value: 'date'}, 
										]"
											item-text="name"
											label="First use by"
											outlined
											:rules="rules.redemption_start_type"

									></v-select>
								</v-col>

								<v-col
										v-if="localPass.redemption_policy.activation_type === 'first_use' &&
											localPass.redemption_policy.expires.type == 'days_after_purchase'"
										sm="4"
								>
									<v-text-field
											v-model.number="localPass.redemption_policy.expires.days"
											class="rounded-lg required"
											hide-details="auto"
											label="Days"
											min="1"
											max="365"
											required
											:rules="rules.redemption_expire_days"
											outlined
											type="number"
											@focus="localPass.redemption_policy.expires.type = 'days_after_purchase'"
											@wheel.native.prevent
									></v-text-field>
								</v-col>
								<v-col
										v-if="localPass.redemption_policy.activation_type === 'first_use' &&
											localPass.redemption_policy.expires.type == 'date'"
										sm="4"
								>
									<bw-date-time-picker
											v-model="localPass.redemption_policy.expires.date"
											class="rounded-lg required"
											label="Date"
											withTime
											:required="true"
											:rules="rules.redemption_start"
											@focus="localPass.redemption_policy.expires.type = 'date'"
									></bw-date-time-picker>
								</v-col>
								<!-- Start Date Specific Date -->
								<v-col
										v-if="localPass.redemption_policy.activation_type === 'date' || localPass.redemption_policy.activation_type === 'date_time'"
										cols="12"
										sm="4"
								>
									<bw-date-time-picker
											v-model="localPass.redemption_policy.start_date"
											class="rounded-lg required"
											label="Date"
											:required="true"
											:rules="rules.redemption_start_date"
											:withTime="localPass.redemption_policy.activation_type === 'date_time'"
									></bw-date-time-picker>
								</v-col>
								
								<!-- End: Start Date Specific Date -->
								<!-- end - first use -->
								<!-- Start Date Upon Purchase -->


							</v-row>
							<v-row>
								<v-col cols="12" sm="4">
									<v-select
											v-model="selectedRedemptionEnd"
											class="rounded-lg required"
											hide-details="auto"
											:items="[
											{name: 'Duration', value: 'duration'}, 
											{name: 'Specific Date', value: 'date'}
										]"
											item-text="name"
											label="End Date"
											outlined
											:rules="rules.redemption_end"
									></v-select>
								</v-col>
								<!-- End Date Duration-->
								<v-col v-if="selectedRedemptionEnd === 'duration'" cols="12" sm="8">
									<v-row>
										<v-col sm="6">
											<v-select
													v-model="localPass.redemption_policy.ends.type"
													class="rounded-lg required"
													label="Duration Type"
													hide-details="auto"
													:items="durationTypes"
													item-text="name"
													outlined
													persistent-hint
													:rules="rules.redemption_ends_type"
											></v-select>
										</v-col>
										<v-col sm="6">
											<v-text-field
													v-model="localPass.redemption_policy.ends.amount"
													class="rounded-lg required"
													label="Duration Amount"
													hide-details="auto"
													outlined
													persistent-hint
													ref="redemptionEndsAmount"
													:rules="redemptionEndsAmountRule"
													:suffix="selectedDurationType"
													type="text"
											></v-text-field>
										</v-col>
									</v-row>
								</v-col>
								<!-- End: End Date Duration-->

								<!-- End Date Specific Date -->
								<v-col
										v-if="selectedRedemptionEnd === 'date'"
										cols="12"
										sm="4"
								>
									<bw-date-time-picker
											v-model="localPass.redemption_policy.ends.date"
											class="rounded-lg required"
											label="Date"
											:required="true"
											:rules="rules.redemption_ends_date"
											withTime
									></bw-date-time-picker>
								</v-col>
								<!-- End: End Date Specific Date -->

							</v-row>
							<form-subsection
									v-if="localPass.no_orders === false"
									class="mt-8"
									title="Update all active passes with the new redemption policy? This will change the redemption policies
									for passholders who have already signed-up/purchased the pass."
							>
								<v-radio-group
										v-model="localPass.redemption_policy_update_past"
										:rules="rules.redemption_policy_update_past"
								>
									<v-radio
											label="Update redemption policy previous and future orders"
											:value="true"
									></v-radio>
									<v-radio
											label="Update redemption policy only for future orders"
											:value="false"
									></v-radio>
								</v-radio-group>
							</form-subsection>
						</ui-box>
					</v-col>
				</v-row>

			</v-form>
			<v-form
					v-model="formIsValid.maxRedemptions"
					ref="maxRedemptionsForm"
					class="mb-8"
			>
				<!-- Advanced Pass Configuration -->
				<v-row v-if="template?.settings?.max_redemptions?.hide === false">
					<v-col>
						<ui-box :form="$refs.maxRedemptionsForm" :eager="!!localPass.id">
							<v-row>
								<v-col>
									<h5>Max Redemptions</h5>
									<span class="grey--text text--darken-1 text-body-2">
										This is the total number of redemptions that can occur on a pass. 
										Once the customer hits this number, the pass will be completed.
									</span>
								</v-col>
							</v-row>
							<!-- Name -->
							<v-divider class="mb-6 mt-4 opacity-80"></v-divider>
							<!-- Max Redemptions -->

							<v-row>
								<v-col cols="12" sm="6" lg="6">
									<v-text-field
											v-model.number="localPass.max_product_redemptions"
											class="rounded-lg"
											hint='Leave blank for unlimited redemptions'
											label="Max Redemptions"
											min="1"
											outlined
											persistent-hint
											:rules="rules.max_redemptions"
											type="number"
											@wheel.native.prevent
									></v-text-field>
								</v-col>
							</v-row>
							<!-- End: Max Redemptions -->
						</ui-box>
					</v-col>
				</v-row>
			</v-form>

			<save-form
					:refs="$refs"
					:additional-validations="[isFormValid]"
					:is-saving="isSaving"
					:save-text="saveText"
					new-forms
					@cancel-action="cancelChanges"
					@save-action="saveDestination"
			></save-form>

		</v-col>
	</v-row>
</template>

<script>
import customizationMixin from './Mixins/customizationMixin';

export default {
	name: 'CustomRedemption',
	mixins: [customizationMixin],
	data() {
		return {
			formIsValid: {
				redemptions: false,
				maxRedemptions: false,
			},
			selectedRedemptionEnd: this.pass?.redemption_policy?.ends?.type
					? (this.pass?.redemption_policy?.ends?.type === 'date' ? 'date' : 'duration')
					: null,
			activationTypes: [
				{ name: 'Upon First Use', value: 'first_use' },
				{ name: 'Upon Purchase', value: 'immediate' },
				{ name: 'Specific Date', value: 'date' },
				{ name: 'Specific Date & Time', value: 'date_time' },
				{ name: 'Date Selection', value: 'selected_date' },
				{ name: 'Date & Time Selection', value: 'selected_date_time' },
			],
			durationTypes: [
				{ name: 'Years', value: 'years' },
				{ name: 'Months', value: 'months' },
				{ name: 'Days', value: 'days' },
				{ name: 'Hours', value: 'hours' },
			],
		};
	},
	mounted() {
		if(this.localPass.max_product_redemptions === 0) {
			this.$set(this.localPass, 'max_product_redemptions', null);
		}

		/*if(this.localPass?.redemption_policy?.activation_type === 'date_time') {
			this.localPass.redemption_policy.activation_type = 'date';
		}*/

		this.$set(this.localPass, 'redemption_policy_update_past', null);
	},
	computed: {
		isFormValid() {
			return this.formIsValid.redemptions && this.formIsValid.maxRedemptions
		},
		redemptionEndsAmountRule() {
			let unit = '';
			let maxUnits = '';
			if(this.localPass.redemption_policy.ends?.type === 'years') {
				unit = 'years';
				maxUnits = 3;
			}
			else if(this.localPass.redemption_policy.ends?.type === 'months') {
				unit = 'months';
				maxUnits = 36;
			}
			else if(this.localPass.redemption_policy.ends?.type === 'days') {
				unit = 'days';
				maxUnits = 1095;
			}
			else if(this.localPass.redemption_policy.ends?.type === 'hours') {
				unit = 'hours';
				maxUnits = 26280;
			}
			const maxRule = (mu) => (v) => v <= mu || `Redemption end ${unit} must be less than or equal to ${mu}.`;
			const rule = [
				v => !!v || `Redemption end ${unit} is required.`,
				v => v >= 1 || `Redemption end ${unit} must be greater than 0.`,
				maxRule(maxUnits),
			];
			return rule;
		},
		selectedDurationType() {
			if(this.localPass.redemption_policy.ends?.type != 'date') {
				return this.localPass.redemption_policy.ends.type
			}
			else {
				return null;
			}
		},
	},
	methods: {
		addRange() {
			if (!this.localPass.active_dates) {
				this.localPass.active_dates = [];
			}

			const { active_dates: activeDates } = this.localPass;

			const startOfDay = moment()
				.tz(this.partner.timezone)
				.startOf('day')
				.utc()
				.format('YYYY-MM-DD h:mm a');

			activeDates.push({ start_date: startOfDay, end_date: "" });
		},
		onEndDateInput(date, activeDates) {
			const endOfDay = moment(date)
				.tz(this.partner.timezone)
				.endOf('day')
				.utc()
				.format('YYYY-MM-DD h:mm a');

			activeDates.end_date = endOfDay;
		},
		removeRange(idx) {
			this.localPass.active_dates.splice(idx, 1);
		}
	},
	watch: {
		'localPass.redemption_policy.ends.type'(val) {
			this.$nextTick(() => { //Validation must run after the rule is recomputed
				this.$refs.redemptionEndsAmount.validate();
			});
		},
		'localPass.redemption_policy.expires.type'(val) {
			if(val === 'date') {
				this.$set(this.localPass.redemption_policy.expires, 'days', null);
			}
			else {
				this.$set(this.localPass.redemption_policy.expires, 'date', null);
			}
		},
		selectedRedemptionEnd: function (val) {
			if(val && val !== 'duration') {
				this.$set(this.localPass.redemption_policy.ends, 'type', 'date');
			}
		},
	},
};

</script>
